import { customElement, property } from 'lit/decorators.js';
import { css, html, LitElement, TemplateResult, unsafeCSS } from 'lit';
import { GzGenericOverlayWebComponent } from '../../gzGenericOverlay';
import { styles as componentStyles } from '../view/fontSizeSelectorStyles.scss';
import { classMap } from 'lit/directives/class-map.js';
import { MDEditorTextSizeOption } from './GzMarkdownEditorToolbarWebComponent';

@customElement('gz-md-editor-font-size-selector-item')
export class GzMarkdownEditorFontSizeSelectorItemWebComponent extends LitElement {
  static get styles() {
    return [unsafeCSS(`${componentStyles}`)];
  }

  @property({ type: Object })
  option: MDEditorTextSizeOption | undefined;

  render() {
    return html`${this.renderFontSizeOption(this.option)}`;
  }

  private splitHashContentAndText(input: string): { hashContent: string; text: string } {
    const match = input.match(/\[.*?\]/);
    const hashContent = match ? match[0] : '';
    const text = input.replace(/\[.*?\]\s*/, '');
    return { hashContent, text };
  }

  private renderFontSizeOption(option: MDEditorTextSizeOption | undefined): TemplateResult | undefined {
    if (!option) {
      return undefined;
    }

    const { hashContent, text } = this.splitHashContentAndText(option.title);
    switch (option.value) {
      case '0':
        return html`<gz-text fontSize="13">${option.title}</gz-text>`;
      case '1':
        return html`<span class="hash">${hashContent}</span
          ><gz-title fontSize="22" lineHeight="1" fontWeight="500">${text}</gz-title>`;
      case '2':
        return html`<span class="hash">${hashContent}</span
          ><gz-title fontSize="18" lineHeight="1" fontWeight="500">${text}</gz-title>`;
      case '3':
        return html`<span class="hash">${hashContent}</span
          ><gz-title fontSize="16" lineHeight="1" fontWeight="500">${text}</gz-title>`;
      case '4':
        return html`<span class="hash">${hashContent}</span><gz-title fontSize="16" lineHeight="1">${text}</gz-title>`;
      default:
        return html`<gz-text fontSize="13">${option.title}</gz-text>`;
    }
  }
}

@customElement('gz-md-editor-font-size-selector')
export class GzMarkdownEditorFontSizeSelectorWebComponent extends LitElement {
  static get styles() {
    return css`
      .selector-target {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-width: 160px;
        height: calc(4 * var(--spacer));
        overflow: hidden;
        cursor: pointer;
        border: 0;
        outline: none;
        background-color: transparent;
        border-radius: var(--input-border-radius);
        transition: background-color 0.2s ease-in-out;
      }
      .selector-target:hover {
        background-color: var(--chart-050-hover);
      }

      .selector-target:focus-visible {
        border: 1px solid var(--blue-layout-500-focus);
      }

      .selected-text-size {
        font-family: var(--font-family-secondary);
        font-size: var(--body-size);
        color: var(--main-titles-inputs-600);
      }

      gz-icon-button {
        height: calc(4 * var(--spacer));
      }
    `;
  }

  @property({ type: Array })
  textSizeOptions: MDEditorTextSizeOption[] = [];

  @property({ type: String })
  selectedTextSize: string;

  private focusedIndex = -1;
  private listItems: NodeListOf<HTMLElement> | null = null;

  render() {
    return html`
      <button class="selector-target" @click="${this.openSelector}" tabindex="0" @keydown="${this.onButtonKeyDown}">
        <span class="selected-text-size">${this.selectedTextSize}</span>
        <gz-icon name="ds-icon-textbox_decrease" color="secondary" size="32"></gz-icon>
      </button>
      <gz-generic-overlay overlayPositionX="left" class="mr-2" id="fs-selector" style="--overlay-min-width: 160px">
        ${this.renderFontSizeList()}
      </gz-generic-overlay>
    `;
  }

  private get overlay(): GzGenericOverlayWebComponent {
    return this.shadowRoot?.getElementById('fs-selector') as GzGenericOverlayWebComponent;
  }

  private async openSelector(event): Promise<void> {
    event.preventDefault();

    this.focusedIndex = 0;
    await this.overlay.openOverlay(this);
    this.listItems = window.document.body.querySelectorAll('gz-md-editor-font-size-selector-item');
    this.updateFocus();
  }

  private handleTextSizeChange(value: string): void {
    this.selectedTextSize =
      this.textSizeOptions.find(option => option.value === value)?.title || this.textSizeOptions[0].title;
    this.dispatchEvent(new CustomEvent('text-size-change', { detail: { value }, bubbles: true, composed: true }));
    this.overlay.closeOverlay();
  }

  private renderFontSizeListItem(option: MDEditorTextSizeOption, index: number): TemplateResult {
    return html`<gz-md-editor-font-size-selector-item
      class=${classMap({
        active: this.selectedTextSize === option.title,
      })}
      @click="${() => this.handleTextSizeChange(option.value)}"
      .option="${option}"
      role="option"
      aria-selected="${this.selectedTextSize === option.title}"
      tabindex="${index === this.focusedIndex ? '0' : '-1'}"
      @keydown="${this.onListKeyDown}"
    ></gz-md-editor-font-size-selector-item>`;
  }

  private renderFontSizeList(): TemplateResult {
    return html` <div role="listbox">
      ${this.textSizeOptions.map((option: MDEditorTextSizeOption, index: number) =>
        this.renderFontSizeListItem(option, index)
      )}
    </div>`;
  }

  private focusNextItem(): void {
    this.focusedIndex = (this.focusedIndex + 1) % this.textSizeOptions.length;
    this.updateFocus();
  }

  private focusPreviousItem(): void {
    this.focusedIndex = (this.focusedIndex - 1 + this.textSizeOptions.length) % this.textSizeOptions.length;
    this.updateFocus();
  }

  private selectFocusedItem(): void {
    const option = this.textSizeOptions[this.focusedIndex];
    this.handleTextSizeChange(option.value);
  }

  private updateFocus(): void {
    if (this.listItems?.length === 0) {
      this.listItems = window.document.body.querySelectorAll('gz-md-editor-font-size-selector-item');
    }

    this.listItems?.forEach((item, index) => {
      if (index === this.focusedIndex) {
        item.setAttribute('tabindex', '0');
        (item as HTMLElement).focus();
      } else {
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  private onButtonKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        this.openSelector(event);
        break;
      case 'Escape':
        this.overlay.closeOverlay();
        break;
    }
  }

  private onListKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        this.focusNextItem();
        break;
      case 'ArrowUp':
        this.focusPreviousItem();
        break;
      case 'Enter':
      case ' ':
        this.selectFocusedItem();
        break;
      case 'Escape':
        this.overlay.closeOverlay();
        break;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-md-editor-font-size-selector': GzMarkdownEditorFontSizeSelectorWebComponent;
    'gz-md-editor-font-size-selector-item': GzMarkdownEditorFontSizeSelectorWebComponent;
  }
}
